import { useField } from "formik";
import React, { useRef, useState } from "react";
import { Input, InputProps } from "reactstrap";
import { ControlButton, IControlStyles, useControlStyles } from "../.";
import { FileHelper } from "../../../../utils/helpers";
import { FileDto } from "../../../../utils/lib";
import { ControlTemplateProps } from "./controlInput";

export interface ControlImageUploadProps extends ControlTemplateProps {
  accept?: string | undefined;
  allowClear?: boolean;
  previewBackgroundColor?: string | undefined;
}

const ComponentImageUploadProps = (
  props: ControlImageUploadProps,
): InputProps => {
  const componentProps = Object.assign({}, props);
  delete componentProps.controlStyles;
  delete componentProps.allowClear;
  delete componentProps.previewBackgroundColor;
  componentProps.name = `${props.name}_Image`;
  componentProps.type = "image";

  return componentProps;
};

const ComponentFileUploadProps = (
  props: ControlImageUploadProps,
): InputProps => {
  const componentProps = Object.assign({}, props);
  delete componentProps.controlStyles;
  delete componentProps.allowClear;
  delete componentProps.previewBackgroundColor;
  componentProps.name = `${props.name}_File`;
  componentProps.type = "file";

  return componentProps;
};

export const ControlImageUpload: React.FC<ControlImageUploadProps> = (
  props,
) => {
  const styles: IControlStyles = props.controlStyles ?? useControlStyles();
  const [, meta, helpers] = useField(props.name ?? "");
  const { value } = meta;
  const { setValue, setTouched } = helpers;
  const [image, setImage] = useState<string | undefined>(undefined);
  const [isDragging, setIsDragging] = useState(false);
  const previewBackgroundColor = props.previewBackgroundColor ?? "#fff";

  const fileInputRef = useRef<HTMLInputElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);

  const allowedExtensions = [".jpg", ".jpeg", ".png", ".bmp", ".gif"];

  const getExtension = (file: File) =>
    `.${file.name.split(".").pop()?.toLowerCase() || ""}`;

  React.useEffect(() => {
    if (value) {
      setImage(FileHelper.FileToBase64String({ image: value }));
    } else {
      setImage(undefined);
    }
  }, [value]);

  const onClear = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    await setTouched(true, false);
    await setValue(null, true);
    setImage(undefined);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleFile = async (file: File) => {
    const ext = getExtension(file);

    await setTouched(true, false);

    if (!allowedExtensions.includes(ext)) {
      setImage(undefined);
      await setValue(file, true);
      return;
    }

    const fileDto: FileDto = await FileHelper.FileToImageFileDto(file);
    const imageSrc: string = await FileHelper.FileToBase64String({
      image: fileDto,
    });

    setValue(fileDto, true);
    setImage(imageSrc);

    if (imageInputRef.current) {
      imageInputRef.current.src = imageSrc;
    }
  };

  const onChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const fileDto: FileDto = await FileHelper.FileToImageFileDto(file);
      const imageSrc: string = await FileHelper.FileToBase64String({
        image: fileDto,
      });
      setValue(fileDto, true);
      setImage(imageSrc);
      if (imageInputRef.current) {
        imageInputRef.current.src = imageSrc;
      }
    }

    if (props.onChange) {
      props.onChange(event);
    }
  };

  const OnSelectImage = () => {
    // Programmatically click the hidden file input
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const onDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(true);
  };

  const onDragEnter = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(true);
  };

  const onDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
  };

  const onDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);

    const file = event.dataTransfer.files?.[0];
    if (file) {
      await handleFile(file);
    }
  };

  return (
    <>
      <Input
        type="file"
        {...ComponentFileUploadProps(props)}
        innerRef={fileInputRef}
        onChange={onChange}
        accept={props.accept ?? "image/*"}
        style={{ display: "none" }}
      />
      <div
        onClick={OnSelectImage}
        onDragEnter={onDragEnter}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        style={{
          display: "inline-block",
          marginTop: 24,
          cursor: "pointer",
          border: isDragging
            ? "2px dashed #007bff"
            : meta?.touched && meta?.error
              ? "2px solid #dc3545"
              : "2px dashed transparent",
          borderRadius: 4,
          padding: 4,
        }}
      >
        {image && (
          <div style={{ position: "relative", display: "inline-block" }}>
            {props.allowClear && (
              <button
                type="button"
                onClick={onClear}
                aria-label="Clear image"
                title="Clear image"
                style={{
                  position: "absolute",
                  top: 6,
                  right: 6,
                  width: 24,
                  height: 24,
                  border: "none",
                  borderRadius: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "transparent",
                  color: "rgba(0,0,0,0.55)",
                  cursor: "pointer",
                  zIndex: 2,
                  lineHeight: 1,
                  padding: 0,
                }}
              >
                X
              </button>
            )}
            <Input
              {...ComponentImageUploadProps(props)}
              name={`${props.name}_${props.name}`}
              innerRef={imageInputRef}
              src={image}
              style={{
                ...(styles.image ?? styles.text ?? styles.input),
                ...props.style,
                backgroundColor: previewBackgroundColor,
              }}
            />
            <div
              style={{
                position: "absolute",
                bottom: 0,
                right: 0,
                backgroundColor: "rgb(0, 0, 0)",
                background: "rgba(0, 0, 0, 0.45)",
                color: "#f1f1f1",
                width: "100%",
                paddingTop: 3,
                height: 25,
                textAlign: "center",
                cursor: "pointer",
                pointerEvents: "none",
              }}
            >
              <span>Change Image or Drop Here</span>
            </div>
          </div>
        )}

        {!image && (
          <ControlButton
            type="button"
            style={{
              ...(styles.button ?? styles.text ?? styles.input),
              ...props.style,
            }}
          >
            {isDragging ? "Drop Image Here" : "Select Image or Drag Here"}
          </ControlButton>
        )}
      </div>
      {meta?.touched && meta?.error && (
        <div className="invalid-feedback" style={{ display: "block" }}>
          {meta?.error}
        </div>
      )}
    </>
  );
};
